import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Plus, Pencil, Trash2, X, Save, ArrowLeft, Upload, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    year: '',
    description: '',
    image: '',
    tags: ''
  });

  const loadProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await fetch(`${API_URL}/api/projects`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setProjects(data);
    } catch (err) {
      toast({ title: "Error", description: "Failed to load projects.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const fd = new FormData();
    fd.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/upload`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setFormData(prev => ({ ...prev, image: data.url }));
      toast({ title: "Uploaded!", description: "Image uploaded successfully." });
    } catch (err) {
      toast({ title: "Error", description: "Image upload failed.", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      location: project.location,
      year: project.year,
      description: project.description,
      image: project.image,
      tags: project.tags ? project.tags.join(', ') : ''
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingProject(null);
    setFormData({ title: '', location: '', year: '', description: '', image: '', tags: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    const tags = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
    const payload = { title: formData.title, location: formData.location, year: formData.year, description: formData.description, image: formData.image, tags };

    try {
      let res;
      if (editingProject) {
        res = await fetch(`${API_URL}/api/projects/${editingProject.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${API_URL}/api/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (!res.ok) throw new Error('Save failed');
      toast({ title: "Success!", description: editingProject ? "Project updated." : "Project added." });
      loadProjects();
      handleCancel();
    } catch (err) {
      toast({ title: "Error", description: "Failed to save project.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      const res = await fetch(`${API_URL}/api/projects/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast({ title: "Deleted", description: "Project removed." });
      loadProjects();
    } catch (err) {
      toast({ title: "Error", description: "Failed to delete project.", variant: "destructive" });
    }
  };

  const getImageSrc = (image) => {
    if (!image) return '';
    if (image.startsWith('http')) return image;
    return `${API_URL}${image}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 data-testid="admin-title" className="text-4xl font-bold text-slate-900 mb-2">Reference Projects Admin</h1>
            <p className="text-slate-600">Manage your portfolio of completed projects</p>
          </div>
          <Button
            data-testid="back-to-website-btn"
            onClick={() => navigate('/')}
            variant="outline"
            className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Website
          </Button>
        </div>

        {/* Add/Edit Form */}
        {isEditing ? (
          <Card className="mb-8 border-2 border-emerald-500" data-testid="project-form-card">
            <CardHeader>
              <CardTitle>{editingProject ? 'Edit Project' : 'Add New Project'}</CardTitle>
              <CardDescription>Fill in the project details below</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="title">Project Title *</Label>
                    <Input
                      data-testid="project-title-input"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="Cryogenic Storage Installation"
                    />
                  </div>
                  <div>
                    <Label htmlFor="location">Location *</Label>
                    <Input
                      data-testid="project-location-input"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      placeholder="Belgrade, Serbia"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="year">Year *</Label>
                    <Input
                      data-testid="project-year-input"
                      id="year"
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      required
                      placeholder="2024"
                    />
                  </div>
                  <div>
                    <Label htmlFor="image-upload">Project Image</Label>
                    <div className="flex gap-2 items-center">
                      <label className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-slate-50 transition-colors text-sm">
                          {isUploading ? (
                            <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</>
                          ) : (
                            <><Upload className="h-4 w-4" /> {formData.image ? 'Change Image' : 'Upload Image'}</>
                          )}
                        </div>
                        <input
                          data-testid="project-image-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </label>
                    </div>
                    {formData.image && (
                      <div className="mt-2">
                        <img
                          src={getImageSrc(formData.image)}
                          alt="Preview"
                          className="h-24 w-36 object-cover rounded border"
                          data-testid="project-image-preview"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    data-testid="project-description-input"
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    placeholder="Detailed description of the project..."
                  />
                </div>

                <div>
                  <Label htmlFor="tags">Tags (comma-separated)</Label>
                  <Input
                    data-testid="project-tags-input"
                    id="tags"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    placeholder="Liquid Argon, Cryogenic, Installation"
                  />
                  <p className="text-sm text-slate-500 mt-1">Separate tags with commas</p>
                </div>

                <div className="flex gap-4">
                  <Button data-testid="project-save-btn" type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={isSaving}>
                    {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    {editingProject ? 'Update Project' : 'Add Project'}
                  </Button>
                  <Button data-testid="project-cancel-btn" type="button" variant="outline" onClick={handleCancel}>
                    <X className="mr-2 h-4 w-4" />
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ) : (
          <div className="mb-8">
            <Button
              data-testid="add-project-btn"
              onClick={() => setIsEditing(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add New Project
            </Button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mx-auto" />
            <p className="text-slate-500 mt-2">Loading projects...</p>
          </div>
        )}

        {/* Projects List */}
        {!isLoading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {projects.map((project) => (
              <Card key={project.id} className="border-2 hover:border-emerald-500 transition-all duration-300" data-testid={`project-card-${project.id}`}>
                <div className="relative h-48 overflow-hidden bg-slate-200">
                  {project.image ? (
                    <img
                      src={getImageSrc(project.image)}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">No Image</div>
                  )}
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">{project.title}</CardTitle>
                  <CardDescription>{project.location} - {project.year}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-3">{project.description}</p>
                  {project.tags && project.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {project.tags.map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">{tag}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      data-testid={`edit-project-${project.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(project)}
                      className="flex-1"
                    >
                      <Pencil className="mr-1 h-3 w-3" /> Edit
                    </Button>
                    <Button
                      data-testid={`delete-project-${project.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(project.id)}
                      className="text-red-600 hover:text-red-700 hover:border-red-600"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!isLoading && projects.length === 0 && !isEditing && (
          <div className="text-center py-12" data-testid="no-projects-message">
            <p className="text-slate-500 text-lg mb-4">No projects yet. Add your first project to get started!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
